import React, { useState, useEffect, useCallback } from 'react';
import { settingsApi, formatApiError } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Hash, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ENTITY_LABELS = {
  customer: 'Kundennummer',
  vehicle:  'Fahrzeugnummer',
  booking:  'Buchungsnummer',
  contract: 'Mietvertrag-Nummer',
  quote:    'Angebot-Nummer',
  credit:   'Gutschrift-Nummer',
  damage:   'Schadens-Nummer',
  inquiry:  'Anfrage-Nummer',
};

const NumberFormatsCard = () => {
  const [formats, setFormats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getNumberFormats();
      setFormats(res.data);
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err) || 'Nummernkreise konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (entity, field, value) => {
    setFormats((prev) => {
      const next = { ...prev };
      next[entity] = { ...next[entity], [field]: value };
      // Local example preview (without re-querying server)
      const f = next[entity];
      const padding = Math.max(1, Math.min(8, parseInt(f.padding) || 4));
      const pad = '1'.padStart(padding, '0');
      const year = new Date().getFullYear();
      next[entity].example = f.with_year
        ? `${f.prefix}-${year}${f.separator || ''}${pad}`
        : `${f.prefix}-${pad}`;
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    if (!formats) return;
    setSaving(true);
    try {
      // Strip server-only "example" field before sending
      const payload = {};
      Object.entries(formats).forEach(([k, v]) => {
        payload[k] = {
          prefix:    String(v.prefix || '').trim() || 'X',
          padding:   Math.max(1, Math.min(8, parseInt(v.padding) || 4)),
          with_year: !!v.with_year,
          separator: String(v.separator || ''),
        };
      });
      const res = await settingsApi.updateNumberFormats(payload);
      setFormats(res.data);
      setDirty(false);
      toast.success('Nummernkreise gespeichert');
    } catch (err) {
      toast.error(formatApiError(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card-swiss p-6">
        <div className="h-6 skeleton mb-4" />
        <div className="h-32 skeleton" />
      </div>
    );
  }

  if (!formats) return null;

  const entries = Object.keys(ENTITY_LABELS);

  return (
    <div className="card-swiss p-6" data-testid="number-formats-card">
      <div className="flex items-start justify-between mb-1 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
            <Hash weight="fill" size={20} className="text-[#E60000]" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Nummernkreise</h2>
            <p className="text-sm text-[#525866]">
              Format für automatisch vergebene Nummern. Manuelle Änderung jederzeit im jeweiligen Detail möglich.
            </p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || !dirty} className="btn-primary" data-testid="save-number-formats">
          <FloppyDisk weight="bold" size={16} className="mr-2" />
          {saving ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-[#525866]">
            <tr>
              <th className="text-left p-2 font-semibold w-44">Nummern-Typ</th>
              <th className="text-left p-2 font-semibold w-28">Präfix</th>
              <th className="text-left p-2 font-semibold w-28">Mit Jahr</th>
              <th className="text-left p-2 font-semibold w-24">Trennzeichen</th>
              <th className="text-left p-2 font-semibold w-28">Stellen</th>
              <th className="text-left p-2 font-semibold">Beispiel (nächste)</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((key) => {
              const f = formats[key] || { prefix: '', padding: 4, with_year: true, separator: '-', example: '' };
              return (
                <tr key={key} className="border-t border-[#E2E4E9]">
                  <td className="p-2 font-medium text-[#111418]">
                    <Label className="block text-xs text-[#8A94A6]">{key.toUpperCase()}</Label>
                    <span>{ENTITY_LABELS[key]}</span>
                  </td>
                  <td className="p-2">
                    <Input
                      value={f.prefix || ''}
                      onChange={(e) => updateField(key, 'prefix', e.target.value.toUpperCase().slice(0, 8))}
                      maxLength={8}
                      className="w-24"
                      data-testid={`nf-prefix-${key}`}
                    />
                  </td>
                  <td className="p-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!f.with_year}
                        onChange={(e) => updateField(key, 'with_year', e.target.checked)}
                        className="h-4 w-4 accent-[#E60000]"
                        data-testid={`nf-year-${key}`}
                      />
                      <span className="text-xs text-[#525866]">Jahr im Code</span>
                    </label>
                  </td>
                  <td className="p-2">
                    <Input
                      value={f.separator || ''}
                      onChange={(e) => updateField(key, 'separator', e.target.value.slice(0, 2))}
                      placeholder="-"
                      className="w-16"
                      disabled={!f.with_year}
                      data-testid={`nf-sep-${key}`}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min={1}
                      max={8}
                      value={f.padding ?? 4}
                      onChange={(e) => updateField(key, 'padding', e.target.value)}
                      className="w-20"
                      data-testid={`nf-pad-${key}`}
                    />
                  </td>
                  <td className="p-2 font-mono text-[#111418]" data-testid={`nf-example-${key}`}>
                    {f.example || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-xs text-[#525866]">
        <strong>Hinweis:</strong> Bestehende Nummern werden nicht verändert. Nur neu vergebene Nummern verwenden das aktualisierte Format.
        Pro Jahr wird der Zähler bei Typen mit „Jahr im Code" automatisch zurückgesetzt.
      </div>
    </div>
  );
};

export default NumberFormatsCard;
